import type { CommunityZonesV2 } from '../../types/api';
import { layerName, textKey } from './builderLayers';
import { CommunityDesignPreview } from './CommunityDesignPreview';
import type { BrandPalette } from './communityZones';

export interface StudioSmallScreenProps {
  zones: CommunityZonesV2;
  palette: BrandPalette;
  imageUrl: string | null;
  isLoadingImage?: boolean;
  logoUrl: string | null;
}

/**
 * The template studio on a phone: read-only where the builder would be
 * (MOBILE_RESPONSIVE_PLAN.md ruling 13).
 *
 * Placing layers on a 330px canvas with a docked inspector is an interaction
 * rewrite for little use, so a phone gets the design as it stands, the AI
 * instructions its text boxes carry (they are written in the box inspector,
 * which a phone does not get), and a plain sentence on where editing happens.
 * Everything the studio holds outside the builder — status, title and
 * description, save, submit, use, delete — stays as it is around this panel.
 */
export function StudioSmallScreen({
  zones,
  palette,
  imageUrl,
  isLoadingImage = false,
  logoUrl,
}: StudioSmallScreenProps) {
  const instructed = zones.texts.filter(
    (text) => text.fill === 'ai' && (text.ai_hint ?? '').trim().length > 0,
  );
  const imageMissing = zones.canvas.kind === 'image' && imageUrl === null;

  return (
    <section className="studio-small" aria-label="Design">
      <div className="studio-small__stage">
        <CommunityDesignPreview
          zones={zones}
          palette={palette}
          imageUrl={imageUrl}
          logoUrl={logoUrl}
          label="The design as it stands"
        />
        {imageMissing ? (
          <span className="builder-stage__note">
            {isLoadingImage ? 'Loading your design' : 'Image backdrop: no design uploaded yet.'}
          </span>
        ) : null}
      </div>

      <div className="studio-small__note" role="note">
        <p className="studio-small__title">Editing a design needs a larger screen.</p>
        <p className="studio-small__body">
          Open this page on a tablet or computer to move layers, change colours or add text. Here
          you can rename it, save it and submit it.
        </p>
      </div>

      {instructed.length > 0 ? (
        <div className="studio-small__hints">
          <p className="studio-small__hints-title">AI instructions</p>
          <dl className="studio-small__list">
            {instructed.map((text) => (
              <div className="studio-small__row" key={text.id}>
                <dt>{layerName(zones, textKey(text.id))}</dt>
                <dd>{text.ai_hint}</dd>
              </div>
            ))}
          </dl>
        </div>
      ) : null}
    </section>
  );
}
